import React, { useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import listAnalytics from './listAnalytics';
import Button from '../../components/Elements/Button/Button';
import TemplateParser from '../../components/Templates/TemplateParser';
import { updatePagination } from '../../actions/environment';
import { unique, difference } from '../../utils/array';
import { addEvent, removeEvent, offset, height } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { urlify } from '../../utils/string';
import s from './Pagination.scss';

let itemsSeen = [];
let itemsSent = [];
let indexes = {};

const Pagination = ({
	ListComponent,
	contentSections,
	contentOffset = 1,
	variantClasses = [],
	pagination,
	setup,
	list,
	cache,
	currentPath,
	showAll = false,
	paging = null,
	currentHash = '',
	canonical = false,
	magazine = false,
	filterOpen = false,
	paginationLoader = false,
	closeSearch = () => {},
	limit = 36,
	layout = 'default',
	...props
}) => {
	const paginationContainer = useRef(null);
	const currentPage = pagination[currentPath] || 1;
	const pageBase = unique(list || cache);
	const listPart = showAll ? pageBase : pageBase.slice(0, currentPage * limit);
	const maxPages = paging ? Math.ceil(paging.total / paging.limit) : Math.ceil(pageBase.length / limit);
	let itemCount = -1 + contentOffset;

	let paginationClasses = [s.pagination];
	if (filterOpen) paginationClasses.push(s.pagination__filterOpen);
	if (pageBase.length < 5) paginationClasses.push(s.pagination__centered);
	if (variantClasses) paginationClasses = [...paginationClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('pagination__') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];

	const handleLoadMore = (automatic = false) => {
		if ((automatic !== true && currentPage === 4 && currentPage < maxPages) || (currentPage !== 4 && currentPage < maxPages)) {
			// 3rd page human must click button
			props.updatePagination(currentPath, currentPage + 1);
			if (paginationLoader !== false) paginationLoader(currentPage + 1);
			return true;
		}
		return false;
	};

	useEffect(() => {
		itemsSeen = [];
		itemsSent = [];
		indexes = {};
	}, [currentPath]);

	useEffect(() => {
		if (paginationContainer.current !== null) {
			const options = {
				root: null,
				rootMargin: '0px',
				threshold: [0.5],
			};

			const handleIntersect = (elements) => {
				elements.forEach((el) => {
					if (el.isIntersecting) {
						const id = el.target.dataset.productId;
						indexes[id] = el.target.dataset.productIndex;
						if (itemsSeen.indexOf(id) === -1) itemsSeen.push(id);
					}
				});
				const itemsToSend = difference(itemsSeen, itemsSent);
				if (itemsToSend.length > 0) {
					listAnalytics(itemsToSend.map((i) => cache[i]), itemsToSend.map((i) => indexes[i]), setup, currentPath || 'productPicker');
					itemsSent = [...itemsSent, ...itemsToSend];
				}
			};

			const observer = new window.IntersectionObserver(handleIntersect, options);
			paginationContainer.current.querySelectorAll('.u_productItem').forEach((e) => observer.observe(e));
		}
	}, [list]);

	useEffect(() => {
		let debounced = false;

		addEvent(window, 'scroll.pagination', () => {
			window.requestAnimationFrame(() => {
				if (!debounced && paginationContainer.current) {
					const paginationTop = offset(paginationContainer.current);
					const paginationHeight = height(paginationContainer.current);

					// Loads in when one window away.
					if (window.pageYOffset + (height(window) * 2) > paginationTop.top + paginationHeight) {
						const shouldBeDebounced = handleLoadMore(true);
						if (shouldBeDebounced) {
							debounced = true;
							window.setTimeout(() => {
								debounced = false;
							}, 500);
						}
					}
				}
			});
		});

		return () => {
			removeEvent(window, 'scroll.pagination');
		};
	});

	const itemsShowing = [];
	let skipProducts = 0;
	let inlineCategoryContentDirection = 'right';
	let subComponent = [];
	const listing = listPart.map((item) => {
		let id;
		let items = false;
		if (list) {
			id = item;
			item = cache[item];
			if (!item) items = Object.keys(cache).filter((cacheID) => cacheID.indexOf(id) !== -1);
		}
		const component = [];

		if (contentSections && contentSections[itemCount]) {
			const inlineCategoryContent = contentSections[itemCount].sections.filter((section) => section.acfCategory);
			if (inlineCategoryContent.length > 0) {
				if (setup.breakPoint !== 'mobile') {
					skipProducts = inlineCategoryContent[0].acfCategory.content.filter((section) => section.content).reduce((acc, section) => (
						parseInt(section?.content?.inlineMedia?.products, 10)
					), 0);
				}
				if (setup.breakPoint === 'tablet' && skipProducts > 1) skipProducts = 1;
				inlineCategoryContentDirection = inlineCategoryContent[0].acfCategory.content.filter((section) => section.content).reduce((acc, section) => (
					section?.content?.inlineMedia?.position
				), '');
			}

			const inlineCategoryContentTemplate = <TemplateParser key={`content_${itemCount}`} sections={contentSections[itemCount].sections} setup={setup} inline inlineCategoryContent={inlineCategoryContentDirection} />;
			if (skipProducts === 0 && inlineCategoryContent.length > 0) component.push(<div className={[s.contentProductCombo, s.contentProductCombo__left].join(' ')}>{ inlineCategoryContentTemplate }</div>);
			else component.push(inlineCategoryContentTemplate);

			itemCount += 1;
		}
		if (skipProducts === 0) subComponent = [];

		if (items) {
			items.forEach((variantID) => {
				const variant = cache[variantID];
				itemsShowing.push(variant);
				component.push(<ListComponent variantClasses={[...variantClasses, skipProducts > 0 ? 'productItem__inlineCategoryContent' : null]} key={variantID} id={variantID} content={variant} canonical={magazine ? translate(variant.uri, setup.language).text : canonical} setup={setup} currentPath={currentPath} currentView={currentPath || 'productPicker'} itemIndex={itemCount + 2} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />);
				itemCount += 1;
			});
		} else if (item) {
			itemsShowing.push(item);
			component.push(<ListComponent variantClasses={[...variantClasses, skipProducts > 0 ? 'productItem__inlineCategoryContent' : null]} key={list ? id : urlify(translate(item.uri, setup.language).text)} id={id} content={item} canonical={magazine ? translate(item.uri, setup.language).text : canonical} setup={setup} currentPath={currentPath} currentView={currentPath || 'productPicker'} itemIndex={itemCount + 2} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />);
			itemCount += 1;
		}

		// Products included in CMS template.
		if (skipProducts > 0) {
			skipProducts -= 1;
			itemCount += 1;
			subComponent = [...subComponent, ...component];
			return skipProducts === 0 ? <div key={itemCount} className={[s.contentProductCombo, s.contentProductCombo__left].join(' ')}>{ subComponent }</div> : null;
		}
		return component;
	});

	return (
		<div className={paginationClasses.join(' ')} ref={paginationContainer}>
			{ listing }
			{
				currentPage < maxPages && !showAll && (
					<div className={s.pagination_viewMore}>
						<Button clickEventHandler={handleLoadMore} language={setup.language}>{ translate(setup, 'loadMore') }</Button>
					</div>
				)
			}
		</div>
	);
};

const mapState = ({ environment }, { showAll = false, paging = null, currentHash = '', canonical = false, magazine = false, ...props }) => ({
	pagination: environment.pagination,
	setup: environment.setup,
	showAll,
	paging,
	currentHash,
	canonical,
	magazine,
	...props,
});

export default hot(module)(connect(mapState, { updatePagination })(Pagination));
